<template>
  <div>
    <div
      v-if="data.status === 'Expired'"
      class="alert alert-danger"
    >
      <icon
        icon="remove"
        style="top: -1px"
      /> This subscription is expired
    </div>
    <div class="row">
      <div class="col-12 col-md-8 border-right">
        <form-control
          v-model="data.name"
          title="Subscription Name"
        />

        <div class="form-group">
          <label>Account</label>
          <autocomplete
            ref="first"
            :input-class="['form-control', {
              'is-invalid': ((error && error.errors.account_id) ? error.errors.account_id : []).length > 0
            }]"
            :class="{
              'is-invalid': ((error && error.errors.account_id) ? error.errors.account_id : []).length > 0
            }"
            :initial-value="data.account ? data.account.id : null"
            :initial-display="data.account ? data.account.name : null"
            class="align-self-center"
            placeholder="Select an account...  "
            source="accounts/search?q="
            results-property="data"
            @selected="selectedAccount"
            @clear="selectedAccount"
          />

          <router-link
            v-if="data.account_id"
            :to="{ name: 'accounts.view', params: { accountId: data.account_id } }"
            class="small"
          >
            View account
          </router-link>

          <form-control-error :errors="(error && error.errors.account_id) ? error.errors.account_id : []" />
        </div>

        <div class="form-group">
          <label>Package</label>
          <multiselect
            v-model="data.roles"
            :options="roles"
            :allow-empty="true"
            :class="{
              'is-invalid': ((error && error.errors.role_id) ? error.errors.role_id : []).length > 0
            }"
            :searchable="true"
            :multiple="true"
            :show-labels="false"
            placeholder="Select a package..."
            class="multiselect"
            style="cursor:pointer"
            select-label="Select"
            label="name"
            track-by="id"
            @input="(option, id) => { data.role_id = option.id }"
          />

          <entitlements
            v-if="data.roles"
            :permissions="allPermissions"
            class="mt-3"
          />

          <form-control-error :errors="(error && error.errors.role_id) ? error.errors.role_id : []" />

          <router-link
            v-if="data.role && $login.can('roles.update')"
            :to="{ name: 'packages.edit', params: { packageId: data.role_id } }"
            class="d-inline-flex mt-1 mb-3 align-items-center btn btn-outline-primary"
          >
            <span class="mr-1">
              Edit package
            </span>
            <icon
              icon="edit"
              class="m-1 text-primary"
            />
          </router-link>
        </div>
      </div>

      <div class="col-12 col-md-4">
        <div class="form-group">
          <label>Start Date</label>

          <div class="block">
            <flat-pickr
              v-model="data.starts_at"
              :config="{enableTime: true}"
              placeholder="Start Date"
            />
          </div>
        </div>
        <div class="form-group">
          <label>End Date</label>

          <div class="block">
            <flat-pickr
              v-model="data.ends_at"
              :config="{enableTime: true}"
              placeholder="End Date"
            />
          </div>
        </div>

        <div class="form-group">
          <label>Plan</label>
          <multiselect
            v-model="data.plan_id"
            :options="['PRO', 'STARTER']"
            :allow-empty="true"
            :show-labels="false"
            :class="{
              'is-invalid': ((error && error.errors['plan_id']) ? error.errors['plan_id'] : []).length > 0
            }"
            :searchable="false"
            placeholder="Select plan"
            class="multiselect pb-0"
            style="cursor:pointer"
            select-label="Select"
          >
          </multiselect>
        </div>

        <div class="m-0 py-3 border-top border-bottom form-group">
          <toggle-button
            :color="{ checked: '#0080a8', unchecked: '#c2c7d9' }"
            v-model="data.is_complimentary"
          />
          Complimentary research
        </div>
        <div class="m-0 py-3 border-bottom form-group">
          <toggle-button
            :color="{ checked: '#0080a8', unchecked: '#c2c7d9' }"
            v-model="data.is_auto_renewing"
          />
          Auto-renew
        </div>
        <div class="m-0 py-3 border-bottom form-group">
          <toggle-button
            :color="{ checked: '#0080a8', unchecked: '#c2c7d9' }"
            v-model="data.is_all_users"
          />
          Access for All Account Users
        </div>
        <div class="m-0 py-3 border-bottom form-group">
          <toggle-button
            :color="{ checked: '#0080a8', unchecked: '#c2c7d9' }"
            v-model="data.is_trial"
          />
          Trial Subscription
        </div>
      </div>
    </div>
    <div
      class="d-flex w-100 border-top mt-4 pt-4 save-footer"
      v-if="!createModal"
    >
      <button
        class="btn btn-outline-danger"
        v-if="!$route.name.includes('create') && $login.can('subscriptions.delete')"
        @click="$modal.show('confirm-delete')"
      >
        Delete subscription
      </button>
      <button
        @click="save"
        class="ml-auto btn btn-primary"
      >
        Save
      </button>
    </div>
  </div>
</template>

<script>
import Entitlements from '@/components/Entitlements'
import Autocomplete from '@/components/Autocomplete'
import moment from 'moment'
import { ToggleButton } from 'vue-js-toggle-button'

export default {
  components: {
    Entitlements,
    Autocomplete,
    ToggleButton,
  },
  props: {
    account: {
      required: false,
      type: Object,
      default: null,
    },
    data: {
      required: false,
      type: Object,
      default () {
        return {
          account_id: this.account ? this.account.id : null,
          account: this.account,
          name: null,
          role_id: null,
          starts_at: moment(),
          ends_at: null,
          is_complimentary: false,
          is_all_users: true,
          is_auto_renewing: true,
          is_trial: false,
        }
      },
    },
    createModal: {
      type: Boolean,
      default: () => false,
    },
  },

  data () {
    return {
      error: null,
      roles: [],
    }
  },

  computed: {
    allPermissions () {
      let permissions = []
      this.data.roles.forEach((role) => {
        permissions = [...permissions, ...role.permissions]
      })

      return permissions
    },
  },

  watch: {
    account () {
      this.data.account = this.account
      this.data.account_id = this.account.id
    },
  },

  beforeMount () {
    this.$http
        .get('roles?populate[]=permissions.entitlement')
        .then((response) => {
          this.roles = response.data.data
        })
  },

  methods: {
    selectedAccount (selection) {
      if (selection) {
        this.data.account = selection.object
        this.data.account_id = selection.object.id
      }
    },

    selectedRole (selection) {
      if (selection) {
        this.data.role = selection.object
        this.data.role_id = selection.object.id
      }
    },

    save () {
      const method = this.data.id ? 'put' : 'post'
      let url = 'subscriptions/'
      url += method === 'put' ? this.data.id : ''

      this.$http[method](url, this.data)
          .then((response) => this.$emit('save-success', response))
          .catch((error) => {
            if (
              error.response &&
            error.response.data &&
            error.response.data.errors
            ) {
              this.error = error.response.data
            }
            this.$emit('save-fail', error)
          })
    },
  },
}
</script>

